import {
  Combobox,
  Input,
  InputBase,
  UnstyledButton,
  useCombobox,
  useComputedColorScheme,
} from '@mantine/core';
import { useState } from 'react';
import type { ComboboxItem } from '@mantine/core';
import { useTranslation } from '@refinedev/core';
import { IconCheck } from '@tabler/icons-react';
import { ClassNames } from '@emotion/react';
import { claimTableSelectFilterStyles } from 'styles/ClaimTableSelectFilterStyles';

export interface ClaimTableSelectFilterProps {
  data: ComboboxItem[];
  value: string | null;
  onChange: (value: string | null) => void;
  placeholder: string;
  disabledOptionTitle?: string;
  onDropdownOpen?: () => void;
  searchable?: boolean;
}

export const ClaimTableSelectFilter = ({
  data,
  value,
  onChange,
  placeholder,
  disabledOptionTitle,
  onDropdownOpen,
  searchable = false,
}: ClaimTableSelectFilterProps) => {
  const { translate } = useTranslation();
  const colorScheme = useComputedColorScheme('dark');
  const [search, setSearch] = useState('');
  const combobox = useCombobox({
    onDropdownOpen,
    onDropdownClose: () => {
      combobox.resetSelectedOption();
      setSearch('');
    },
  });
  const selectedOption = data.find(
    (option) => option.value === value && !option.disabled
  );

  const visibleOptions = data.filter((option) =>
    option.label.toLocaleLowerCase().includes(search.trim().toLocaleLowerCase())
  );

  return (
    <ClassNames>
      {({ css }) => {
        const classes = {
          dropdown: css(claimTableSelectFilterStyles.dropdown),
          options: css(claimTableSelectFilterStyles.options),
          option: css(claimTableSelectFilterStyles.option),
          indicator: css(claimTableSelectFilterStyles.indicator),
          clear: css(claimTableSelectFilterStyles.clear),
        };
        return (
          <Combobox
            store={combobox}
            width={236}
            position="bottom-start"
            withinPortal
            onOptionSubmit={(nextValue) => {
              if (
                !data.some(
                  (option) => option.value === nextValue && !option.disabled
                )
              )
                return;
              onChange(nextValue === value ? null : nextValue);
            }}
          >
            <Combobox.Target targetType="button">
              <InputBase
                component="button"
                type="button"
                variant="unstyled"
                className="mrt-filter-text-input"
                styles={{
                  root: claimTableSelectFilterStyles.getControl(colorScheme),
                }}
                rightSection={<Combobox.Chevron />}
                rightSectionPointerEvents="none"
                aria-label={placeholder}
                title={placeholder}
                onClick={(event) => {
                  event.stopPropagation();
                  combobox.toggleDropdown();
                }}
              >
                {selectedOption?.label ?? value ?? (
                  <Input.Placeholder>{placeholder}</Input.Placeholder>
                )}
              </InputBase>
            </Combobox.Target>
            <Combobox.Dropdown className={classes.dropdown}>
              {searchable && (
                <Combobox.Search
                  value={search}
                  onChange={(event) => {
                    setSearch(event.currentTarget.value);
                    combobox.resetSelectedOption();
                  }}
                  placeholder={translate(
                    'pages:claims.tableSelectFilter.search',
                    'Search options'
                  )}
                  aria-label={translate(
                    'pages:claims.tableSelectFilter.search',
                    'Search options'
                  )}
                />
              )}
              <Combobox.Options
                className={classes.options}
                aria-label={placeholder}
              >
                {visibleOptions.map((option) => (
                  <Combobox.Option
                    key={option.value}
                    value={option.value}
                    disabled={option.disabled}
                    aria-disabled={option.disabled || undefined}
                    aria-selected={value === option.value}
                    active={value === option.value}
                    className={classes.option}
                    title={option.disabled ? disabledOptionTitle : undefined}
                  >
                    <span className={classes.indicator} aria-hidden="true">
                      {value === option.value && (
                        <IconCheck size={11} stroke={3} />
                      )}
                    </span>
                    <span>{option.label}</span>
                  </Combobox.Option>
                ))}
                {visibleOptions.length === 0 && (
                  <Combobox.Empty>
                    {translate(
                      'pages:claims.tableSelectFilter.noOptions',
                      'No options found'
                    )}
                  </Combobox.Empty>
                )}
              </Combobox.Options>
              <UnstyledButton
                className={classes.clear}
                onClick={() => {
                  onChange(null);
                  combobox.closeDropdown();
                }}
              >
                {translate(
                  'pages:claims.tableSelectFilter.clearSelection',
                  'Clear selection'
                )}
              </UnstyledButton>
            </Combobox.Dropdown>
          </Combobox>
        );
      }}
    </ClassNames>
  );
};
